<template>
	<div v-if="visibleMessages.length" class="message-banners">
		<LBanner
			v-for="message in visibleMessages"
			:key="message.ID"
			:title="message.Title"
			:tone="severity(message).BannerTone"
			:icon="severity(message).Icon"
			:dismissible="message.IsDismissible"
			:expandable="isExpandable(message)"
			:dismiss-label="'Dismiss: ' + message.Title"
			@open="openMessage(message)"
			@dismiss="dismiss(message)"
		/>

		<LActionLink v-if="hiddenCount > 0" class="more-link" @click="goToMessages">
			{{ hiddenCountLabel }}
		</LActionLink>
	</div>

	<SystemMessageModal v-model:open="isModalOpen" :message="openedMessage" @dismiss="dismiss" />
</template>










<script setup>
/**
 * The system-message banners at the top of every signed-in page.
 *
 * Mounted once in PrimaryLayout, which survives route changes, so the list is fetched once
 * per session rather than on every navigation. The list itself is the shared ref in
 * system-messages-data.js, so a dismissal made on the Messages page clears the banner here
 * too without a reload.
 *
 * At most MAX_VISIBLE_BANNERS are shown. Past that the shell would be more notice than
 * page, so the rest collapse into a link to the Messages page — nothing is dropped, and
 * the reader can still get to all of it.
 */
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { activeMessages, loadActiveMessages, dismissMessage } from '../system-messages-data.js';
import { resolveSystemMessageSeverity } from '../system-messages-display-values.js';
import SystemMessageModal from './SystemMessageModal.vue';
import { LBanner, LActionLink } from '/components2/index.js';

const router = useRouter();

const MAX_VISIBLE_BANNERS = 3;




//-- Data/Reactive State
const isModalOpen = ref(false);
const openedMessage = ref(null);




//-- Computed Properties
const visibleMessages = computed(() => activeMessages.value.slice(0, MAX_VISIBLE_BANNERS));


const hiddenCount = computed(() => Math.max(activeMessages.value.length - MAX_VISIBLE_BANNERS, 0));


const hiddenCountLabel = computed(() =>
{
	const count = hiddenCount.value;

	return count == 1 ? "1 more message" : count + " more messages";
});




//-- Methods

// Always have an init() that get's called from onMounted()
const init = async () =>
{
	await loadActiveMessages();
};


const severity = (message) => resolveSystemMessageSeverity(message.SeverityCode);


/**
 * Only a message with more to say than its title is worth opening — a one-line notice
 * would show a "Read more" that led to the same sentence again.
 */
const isExpandable = (message) => !!message.Body || !!message.LinkUrl;


const openMessage = (message) =>
{
	openedMessage.value = message;
	isModalOpen.value = true;
};


const dismiss = async (message) =>
{
	if (message == null) return;

	await dismissMessage(message.ID);
};


const goToMessages = async () =>
{
	await router.push({ path: "/my-profile/messages" });
};




//-- Lifecycle Hooks

// Always have onMounted() and it must call init().
onMounted(async () =>
{
	await init();
});
</script>










<style scoped>
.message-banners
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-2);
	margin-bottom: var(--sp-4);
}

.more-link
{
	align-self: flex-start;
	font-size: var(--text-chip);
}
</style>
